import { SimpleGrid, Skeleton, useToast } from '@chakra-ui/react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  finleyAssert,
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import { parsePortfolioSummaryPropertiesFromCreditAgreement } from '../shared/portfolioChartUtils';
import { PortfolioGridItem } from '../shared/PortfolioGridItem';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from '~/packages/common-frontend/utils';
import { useGetCurrentCreditAgreementFullArtifactQuery } from '~/packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { useGetLatestLedgersForCreditFacilitiesQuery } from '~/packages/statements/requests/ledger.gql.generated';

export type Dashboard = 'borrower' | 'capitalProvider';

type PortfolioSummaryOverviewCardsProps = {
  capitalProviderOrganizationIds: string[];
  creditFacilityId: string;
  dashboard: Dashboard;
};
export const PortfolioSummaryOverviewCards = ({
  capitalProviderOrganizationIds,
  creditFacilityId,
  dashboard,
}: PortfolioSummaryOverviewCardsProps) => {
  const toast = useToast();
  const navigate = useNavigate();

  const countersignerOrganizationIds =
    dashboard === 'capitalProvider'
      ? capitalProviderOrganizationIds
      : undefined;

  const {
    loading: creditAgreementArtifactLoading,
    data: creditAgreementArtifactData,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
    },
  });

  const {
    loading: ledgerLoading,
    data: ledgerData,
  } = useGetLatestLedgersForCreditFacilitiesQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityIds: [creditFacilityId],
      countersignerOrganizationIds,
      ledgerAccountGroup: 'expensesFees',
    },
  });

  if (
    creditAgreementArtifactLoading ||
    !creditAgreementArtifactData ||
    !creditAgreementArtifactData.currentCreditAgreementFullArtifact ||
    ledgerLoading ||
    !ledgerData
  ) {
    return <Skeleton height="150px" />;
  }

  const latestLedgerTransaction =
    ledgerData.latestLedgersForCreditFacilities[0]?.ledger;

  const { currentCreditAgreementFullArtifact } = creditAgreementArtifactData;
  let parsedCreditAgreementFullArtifact = null;

  try {
    const { creditAgreement } = currentCreditAgreementFullArtifact;
    const parsedCreditAgreement = parseCreditAgreementFullArtifact(
      creditAgreement,
    );
    parsedCreditAgreementFullArtifact = parsedCreditAgreement;
  } catch (err) {
    const errorToastTitle = `Error parsing credit agreement artifacts for creditFacilityId ${creditFacilityId}`;
    toast({
      ...errorToastConfig,
      title: errorToastTitle,
      id: 'errorParsingCreditAgreement',
    });
    navigate('/');
  }

  finleyAssert(
    parsedCreditAgreementFullArtifact,
    `Expect creditAgreementFullArtifact to exist for creditFacilityId ${creditFacilityId}`,
  );
  const {
    closingDate,
    maturityDate,
    interestRate,
  } = parsePortfolioSummaryPropertiesFromCreditAgreement(
    parsedCreditAgreementFullArtifact,
    capitalProviderOrganizationIds,
  );

  const interestAndFeesBalance = latestLedgerTransaction
    ? latestLedgerTransaction.balance
    : 0;

  return (
    <SimpleGrid columns={{ sm: 2, md: 4, lg: 4, xl: 4 }} gap="1.5rem">
      <PortfolioGridItem
        label="Closing Date"
        value={commonDate.fullDateStrToHumanDateString(closingDate)}
      />
      <PortfolioGridItem
        label="Maturity Date"
        value={commonDate.fullDateStrToHumanDateString(maturityDate)}
      />
      <PortfolioGridItem
        label="Accrued Interest & Fees"
        value={commonUtils.numberToCurrencyWithSymbol({
          number: interestAndFeesBalance,
        })}
      />
      <PortfolioGridItem label="Floating Interest Rate" value={interestRate} />
    </SimpleGrid>
  );
};
